/**
 * useIrrigationLive
 *
 * One hook behind the irrigation landing page: fetches the farm overview, then
 * the per-controller schedule state, and folds them into "what is watering
 * right now".
 *
 * Two different clocks run here on purpose. The network poll is slow (20 s) —
 * relay state does not change faster than that and every extra request costs a
 * farm on a metered link. The countdown tick is fast (1 s) and purely local: it
 * re-derives elapsed/remaining from the last payload so the numbers move
 * smoothly instead of jumping once per poll.
 *
 * Which schedule feed a controller gets depends on where its timers live. An
 * ESP-NOW master pushes windows down to its nodes, so the nodes are asked what
 * they hold. An XBee site's laptop bridge holds the timers itself and nothing
 * is ever pushed, so its nodes hold nothing to report — that site's schedule is
 * read from the server's own view of today instead. One request per controller
 * either way; `controller.transport`, which the overview carries, picks which.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import irrigationOverviewService, {
  type IrrigationOverviewData,
} from '../services/irrigationOverview';
import espnowService, { type NodeScheduleState } from '../services/espnowIrrigation';
import {
  buildLiveState,
  type ControllerTimeline,
  type IrrigationLiveState,
} from '../utils/irrigationRuns';
import { useOrgTimezone, tzOffsetSec } from '../utils/timezone';

const POLL_MS = 20_000;
const TICK_MS = 1_000;

interface UseIrrigationLiveResult {
  data: IrrigationOverviewData | null;
  live: IrrigationLiveState;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refresh: () => Promise<void>;
}

export function useIrrigationLive(): UseIrrigationLiveResult {
  const orgTz = useOrgTimezone();
  const [data, setData] = useState<IrrigationOverviewData | null>(null);
  const [schedules, setSchedules] = useState<Record<string, NodeScheduleState[]>>({});
  const [timelines, setTimelines] = useState<Record<string, ControllerTimeline>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Guards a late response from an unmounted page overwriting fresh state.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const overview = await irrigationOverviewService.getOverview();
      if (!alive.current) return;
      setData(overview);
      setError(null);

      const controllers = [
        ...overview.farms.flatMap((f) => f.controllers),
        ...overview.unassigned.controllers,
      ];

      // Schedule feeds are per controller and best-effort: one node that will
      // not answer must not blank out the runs we can see on every other.
      const bridgeIds = controllers
        .filter((c) => c.transport === 'xbee')
        .map((c) => c.controller_id);
      const nodeIds = controllers
        .filter((c) => c.transport !== 'xbee')
        .map((c) => c.controller_id);

      const [nodeResults, bridgeResults] = await Promise.all([
        Promise.all(
          nodeIds.map(async (id) => {
            try {
              return [id, await espnowService.getScheduleState(id)] as const;
            } catch {
              return [id, [] as NodeScheduleState[]] as const;
            }
          })
        ),
        Promise.all(
          bridgeIds.map(async (id) => {
            try {
              const map = await espnowService.getLiveMap(id);
              // Stamped on arrival, not before the call: the countdown ticks
              // from this instant, so a slow response must not be counted as
              // schedule time already elapsed.
              const snapshot: ControllerTimeline = {
                timeline: map.timeline,
                nowSec: map.now_sec,
                fetchedAtMs: Date.now(),
                routers: map.routers,
              };
              return [id, snapshot] as const;
            } catch {
              return [id, null] as const;
            }
          })
        ),
      ]);
      if (!alive.current) return;
      setSchedules(Object.fromEntries(nodeResults));
      setTimelines(
        Object.fromEntries(
          bridgeResults.filter((r): r is readonly [string, ControllerTimeline] => r[1] != null)
        )
      );
      setLastUpdated(new Date());
      setNow(Date.now());
    } catch {
      if (!alive.current) return;
      setError('Failed to load irrigation status');
    } finally {
      if (alive.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    void load();
    const poll = setInterval(() => void load(true), POLL_MS);
    return () => clearInterval(poll);
  }, [load]);

  // Local countdown tick — no network, just re-projects the last payload.
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(tick);
  }, []);

  // A bridge timeline carries UTC times for programs chosen by LOCAL weekday,
  // so folding them into one frame needs the site's offset. Recomputed with the
  // 1 s tick rather than memoised on the timezone alone, so a site crossing its
  // daylight-saving boundary while the page is open picks the new offset up on
  // the next tick instead of staying an hour out until a reload.
  const offsetSec = useMemo(() => tzOffsetSec(orgTz, new Date(now)), [orgTz, now]);

  const live = useMemo(
    () => buildLiveState(data, schedules, timelines, now, offsetSec),
    [data, schedules, timelines, now, offsetSec]
  );

  const refresh = useCallback(() => load(true), [load]);

  return { data, live, loading, refreshing, error, lastUpdated, refresh };
}

export default useIrrigationLive;
